/**
 * interior_style.ts — the Cosmos interior STYLE as data (interiors programme, slice D, 2026-10-01). PURE: no THREE, no
 * DOM, no rows — `verify:interior-style` pins every rule here; `modules/interior/interior_look.ts` draws from it.
 *
 * style.md is the canon ("a working, welcoming settler-tech room: pale alloy and teal bio-glass, warm amber light, and
 * objects worn by everyday use"; clean and legible above, worn at hand height; two construction traditions that stay
 * recognisable in greyscale; amber marks habitation in BOTH; cyan is light, never paint). This file turns it into:
 *
 *   ROLES        the shared material family (construction, wood, cloth, metal, light, glass) — world colours, data.
 *   IDENTITY     style.md §4.1 verbatim (civ colours, cloth, stone, sign ink / paper). NEVER invented, never a UI accent.
 *   TREATMENTS   what a tradition BUILDS a room from: wall field + frame + skirting + dado, floor, ceiling, trim, stair,
 *                glass, sign — Aurelian (warm, ordered, gilded), Borean (cool, austere, prepared), neutral (an
 *                unaligned builder: settler-tech with no colour of its own). A PLAYER CIVILISATION chooses one of the
 *                three (`player_civ_style`, neutral until it does) and wears its own banner as the accent
 *                (`bannerAccent`: trim and signs — lines, never a field).
 *   EMPHASIS     what a PURPOSE asks of that construction: a home's soft layers, a cantina's worn wood, a clinic's
 *                maintained clean surfaces, a workshop's darker service base, a civic hall's stone and oiled wood.
 *   WEAR         dust and scuffs at the base and at hand height — never on a treatment (clinic) surface.
 *
 * `styleFor(look)` resolves in that order — source pack / civ → owner treatment → purpose emphasis → a small seeded
 * local variation (a value nudge and a pattern phase: it never changes a role, a meaning or a sign).
 *
 * THE LEVER: `localStorage atlas:interior-look = 'off'` returns exactly the pre-programme look and room response
 * (`interiorLookEnabled`). Hex values are sRGB references for surfaces, not measured albedo.
 */
import type { InteriorPurpose } from './interior_purpose.js';

// ─── colour arithmetic (sRGB hex strings; pure) ──────────────────────────────

export type Rgb = [number, number, number];
export function hexToRgb(hex: string): Rgb {
  const h = hex.replace('#', '');
  const n = parseInt(h.length === 3 ? h.split('').map((c) => c + c).join('') : h, 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}
export function rgbToHex(r: number, g: number, b: number): string {
  const c = (v: number): string => Math.max(0, Math.min(255, Math.round(v))).toString(16).padStart(2, '0');
  return `#${c(r)}${c(g)}${c(b)}`;
}
/** A straight sRGB-space mix (a paint mix by eye — these are references, not light). */
export function mixHex(a: string, b: string, t: number): string {
  const x = hexToRgb(a), y = hexToRgb(b);
  return rgbToHex(x[0] + (y[0] - x[0]) * t, x[1] + (y[1] - x[1]) * t, x[2] + (y[2] - x[2]) * t);
}
export function scaleHex(a: string, k: number): string { const x = hexToRgb(a); return rgbToHex(x[0] * k, x[1] * k, x[2] * k); }
const lin = (c: number): number => { const v = c / 255; return v <= 0.04045 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4; };
/** WCAG relative luminance (linear, 0..1) — "does it read in greyscale" is asked of this. */
export function luminance(hex: string): number { const [r, g, b] = hexToRgb(hex); return 0.2126 * lin(r) + 0.7152 * lin(g) + 0.0722 * lin(b); }
export function contrast(a: string, b: string): number { const x = luminance(a), y = luminance(b); return (Math.max(x, y) + 0.05) / (Math.min(x, y) + 0.05); }
/** 0 = grey … 1 = fully saturated (HSV saturation) — "no broad cyan paint" is asked of this. */
export function saturation(hex: string): number { const [r, g, b] = hexToRgb(hex); const mx = Math.max(r, g, b), mn = Math.min(r, g, b); return mx === 0 ? 0 : (mx - mn) / mx; }

// ─── identity (style.md §4.1 — verbatim; "never themed") ─────────────────────

export type CivId = 'a' | 'b';
/** The canon identity colours (`ui_theme.ts` `--ax-civ-a/b`): Aurelian gold, Borean ice. */
export const CIV_IDENTITY: Readonly<Record<CivId, string>> = { a: '#e6b95a', b: '#6db3f2' };
/** Settlement cloth / banners (`settlement_dressing.ts`). */
export const CIV_CLOTH: Readonly<Record<CivId, string>> = { a: '#d9a94e', b: '#7fc4e8' };
/** Town wall stone: Aurelian warm tan, Borean slate (`settlement_dressing.ts`). */
export const CIV_STONE: Readonly<Record<CivId, string>> = { a: '#b09468', b: '#7d8a94' };
/** The reference art palette (style.md §2, cut from the paintings): everyone's. */
export const REFERENCE_PALETTE = { ink: '#120d0a', rust: '#b4462a', brass: '#d9a441', bone: '#efe6d2', dust: '#8c7a66', cyan: '#5fa8c9' } as const;

export type Tradition = 'aurelian' | 'borean' | 'neutral';
export const TRADITIONS: readonly Tradition[] = ['aurelian', 'borean', 'neutral'];
/** Sign planks, ink / paper (style.md §4.1; `settlement_dressing.plank`). A neutral builder writes in the frontier's own ink on bone. */
export const SIGN_PALETTE: Readonly<Record<Tradition, { ink: string; paper: string }>> = {
  aurelian: { ink: '#3a2c12', paper: '#d9c08a' },
  borean: { ink: '#12262e', paper: '#a8c9d8' },
  neutral: { ink: REFERENCE_PALETTE.ink, paper: REFERENCE_PALETTE.bone },
};

// ─── roles: the shared settler-tech family (style.md §5; world data) ─────────

export const ROLES = {
  /** Main construction: matte chalky pale alloy, grey-cream to off-white. */
  paleAlloy: '#d2cfc4', paleAlloyWarm: '#dccba8', paleAlloyCool: '#97a3ab',
  /** The dark structure every panel is framed by: door frames, bases, joints. */
  gunmetal: '#454b53', gunmetalDark: '#2b3036',
  /** Oiled wood for furniture and ceremony: honey, walnut, the dark oiled ceremony wood. */
  honey: '#a67c52', walnut: '#6b4e36', darkOiled: '#4f3a26',
  /** Dressed stone where no civ's own stone applies (`settlement_dressing` stone). */
  paleStone: '#9a9d9f',
  /** Cloth: rust / moss / cream / ochre / sage — removable, never a wall colour. */
  cloth: { rust: REFERENCE_PALETTE.rust, moss: '#6f7f4a', cream: '#e8e2d4', ochre: '#c9973f', sage: '#9aa88a' },
  /** Metal detail: the welded brass seam on every made thing; copper for pipes and bands. */
  brass: REFERENCE_PALETTE.brass, copper: '#b06a3c',
  /** Habitation light: amber, in BOTH civilisations (the town lantern's own colour). */
  amber: '#ffb45e', amberSoft: '#ffcf8a',
  /** Glass and screens: teal bio-glass; the engines' cyan is LIGHT, never paint. */
  tealGlass: '#8fd8cc', iceGlass: '#a6d6ee', screenCyan: REFERENCE_PALETTE.cyan,
} as const;

// ─── purposes → emphasis families ────────────────────────────────────────────

export type PurposeFamily = 'home' | 'social' | 'clinic' | 'work' | 'civic' | 'trade' | 'plain';
export const PURPOSE_FAMILY: Readonly<Record<InteriorPurpose, PurposeFamily>> = {
  residence: 'home', apartment: 'home', lodge: 'home',
  hospitality: 'social', theater: 'social',
  clinic: 'clinic',
  workshop: 'work', garage: 'work', shed: 'work',
  civic: 'civic', guild: 'civic', office: 'civic',
  retail: 'trade', bank: 'trade', transit: 'trade',
  generic: 'plain',
};
export const FAMILIES: readonly PurposeFamily[] = ['home', 'social', 'clinic', 'work', 'civic', 'trade', 'plain'];

// ─── the treatment types ─────────────────────────────────────────────────────

export type DadoPattern = 'plain' | 'boards' | 'blocks' | 'kick';
export type FloorKind = 'plank' | 'stone' | 'alloy';
export interface WallTreatment {
  /** The upper wall field (1.0 m up to the ceiling) — the big readable surface. Stays composed and clean. */
  field: string;
  /** The lower wall, floor to ~1 m: the band hands, boots and chair backs reach. */
  dado: string;
  dadoPattern: DadoPattern;
  /** Seams, the window sill and head lines, the dado cap: the dark structure that frames the panels. */
  frame: string;
  skirting: string;
  /** The thin line under the dado cap: the builder's identity (§4.1 gold / ice) or everyone's brass. */
  capLine: string;
  /** Panel width in metres (a 2.5 m tile divides by it). */
  panelM: number;
  /** Seam width in metres. */
  seamM: number;
  /** A lighter pilaster strip at each 2.5 m bay (the Aurelian order). */
  pilaster: boolean;
  rivets: boolean;
}
export interface FloorTreatment { kind: FloorKind; base: string; joint: string }
export interface CeilingTreatment { field: string; seam: string; panelM: number; beams: boolean }
export interface TrimTreatment {
  /** Rails, columns, shelves: the frame material. */
  body: string;
  /** The hand rail's worn metal cap (≈ 1 m). */
  cap: string;
  /** The thin warm strip light under the ceiling: amber = people live here. */
  strip: string;
}
export interface StairTreatment { kind: FloorKind; tread: string; riser: string }
export interface GlassTreatment { color: string; opacity: number }
export interface WearRule {
  /** Dust and scuffs where boots and mops reach (0–0.35 m): 0 none … 1 heavy. */
  base: number;
  /** Rubs and scuffs where hands, shoulders and chair backs reach (0.75–1.3 m). */
  hand: number;
}
/** A sign's colours: ink on paper, and — only for a player civilisation — the board's FRAME in its banner accent. */
export interface SignPalette { ink: string; paper: string; frame?: string }
/** A lift (wave 3): gunmetal frame, a warm cab, an amber call LIGHT, and the call plate's RING (paint: the builder's
 *  identity — a player civilisation's banner accent; the light itself is always amber). */
export interface LiftTreatment { frame: string; cab: string; door: string; call: string; ring: string }
/** A partition and a rented unit's door (wave 3): its number plate is dynamic text in the sign's palette. */
export interface PartitionTreatment { panel: string; door: string; frame: string; plate: SignPalette }

export interface Treatment {
  wall: WallTreatment; floor: FloorTreatment; ceiling: CeilingTreatment; trim: TrimTreatment; stair: StairTreatment;
  glass: GlassTreatment; sign: SignPalette; wear: WearRule; lift: LiftTreatment; partition: PartitionTreatment;
}

// ─── the three construction traditions ───────────────────────────────────────

/**
 * AURELIAN — warm, ordered, gilded: warm stone and pale alloy, gold trim, columned halls. A LIGHT room: a pale warm
 * field in wide ordered bays between pilasters, the town's own tan stone as the dado, honey wood, a thin gold line.
 * BOREAN — cool, austere, prepared: slate, thick walls, insulated panels in dark framing. A room of MID fields and DARK
 * structure: narrow riveted panels, the town's slate as the dado, dark floors — and warm amber where people sit.
 * NEUTRAL — settler-tech with no flag: chalky pale alloy in gunmetal frames, walnut underfoot, everyone's brass.
 */
export const TRADITION_BASE: Readonly<Record<Tradition, Treatment>> = {
  aurelian: {
    wall: { field: ROLES.paleAlloyWarm, dado: CIV_STONE.a, dadoPattern: 'blocks', frame: '#8a6b4a', skirting: '#6b5238', capLine: CIV_IDENTITY.a, panelM: 2.5, seamM: 0.012, pilaster: true, rivets: false },
    floor: { kind: 'plank', base: '#b08a5a', joint: '#6b5238' },
    ceiling: { field: '#ddd3bd', seam: '#c9bd9f', panelM: 1.25, beams: false },
    trim: { body: '#9a7448', cap: CIV_IDENTITY.a, strip: ROLES.amber },
    stair: { kind: 'plank', tread: '#9c784e', riser: CIV_STONE.a },
    glass: { color: '#a8dcc8', opacity: 0.11 },
    sign: SIGN_PALETTE.aurelian,
    wear: { base: 0.55, hand: 0.6 },
    lift: { frame: '#8a6b4a', cab: '#e0d5bd', door: '#c9b891', call: ROLES.amber, ring: CIV_IDENTITY.a },
    partition: { panel: '#d8ccb2', door: ROLES.honey, frame: '#8a6b4a', plate: SIGN_PALETTE.aurelian },
  },
  borean: {
    wall: { field: ROLES.paleAlloyCool, dado: CIV_STONE.b, dadoPattern: 'blocks', frame: ROLES.gunmetalDark, skirting: '#23272c', capLine: CIV_IDENTITY.b, panelM: 0.625, seamM: 0.03, pilaster: false, rivets: true },
    floor: { kind: 'stone', base: '#5d666e', joint: '#2b3036' },
    ceiling: { field: '#aeb8be', seam: '#3a4048', panelM: 1.25, beams: true },
    trim: { body: '#343a42', cap: ROLES.brass, strip: ROLES.amber },
    stair: { kind: 'stone', tread: '#566068', riser: ROLES.gunmetalDark },
    glass: { color: ROLES.iceGlass, opacity: 0.12 },
    sign: SIGN_PALETTE.borean,
    wear: { base: 0.6, hand: 0.55 },
    lift: { frame: ROLES.gunmetalDark, cab: '#8e7a62', door: '#7d8a94', call: ROLES.amber, ring: ROLES.brass },
    partition: { panel: '#8b979f', door: ROLES.walnut, frame: ROLES.gunmetalDark, plate: SIGN_PALETTE.borean },
  },
  neutral: {
    wall: { field: ROLES.paleAlloy, dado: '#b9b5a8', dadoPattern: 'kick', frame: ROLES.gunmetal, skirting: '#3a4047', capLine: ROLES.brass, panelM: 1.25, seamM: 0.018, pilaster: false, rivets: false },
    floor: { kind: 'plank', base: '#96714c', joint: ROLES.darkOiled },
    ceiling: { field: '#d3d0c5', seam: '#a9a69b', panelM: 1.25, beams: false },
    trim: { body: ROLES.gunmetal, cap: ROLES.brass, strip: ROLES.amber },
    stair: { kind: 'plank', tread: ROLES.walnut, riser: '#3a4047' },
    glass: { color: ROLES.tealGlass, opacity: 0.12 },
    sign: SIGN_PALETTE.neutral,
    wear: { base: 0.6, hand: 0.6 },
    lift: { frame: ROLES.gunmetal, cab: '#c9b891', door: '#b9b5a8', call: ROLES.amber, ring: ROLES.brass },
    partition: { panel: '#c6c3b8', door: ROLES.walnut, frame: ROLES.gunmetal, plate: SIGN_PALETTE.neutral },
  },
};

/** The wood a tradition panels a social room's dado with. */
const WAINSCOT: Readonly<Record<Tradition, string>> = { aurelian: ROLES.honey, borean: '#5a4230', neutral: ROLES.walnut };
/** The dressed stone of a civic room: the civ's own (§4.1), else the frontier's pale stone. */
const CIVIC_STONE: Readonly<Record<Tradition, string>> = { aurelian: CIV_STONE.a, borean: CIV_STONE.b, neutral: ROLES.paleStone };
/** Stone and sealed floors by tradition (lighter where the tradition is light). */
const STONE_FLOOR: Readonly<Record<Tradition, FloorTreatment>> = {
  aurelian: { kind: 'stone', base: '#cbbd9f', joint: '#9a8a6a' },
  borean: { kind: 'stone', base: '#5d666e', joint: '#2b3036' },
  neutral: { kind: 'stone', base: '#aaa596', joint: '#6f6b60' },
};
const SEALED_FLOOR: Readonly<Record<Tradition, FloorTreatment>> = {
  aurelian: { kind: 'alloy', base: '#9a917f', joint: '#6b6455' },
  borean: { kind: 'alloy', base: '#474d55', joint: '#23272c' },
  neutral: { kind: 'alloy', base: '#7c8086', joint: '#4a4f57' },
};
const CLEAN_FLOOR: Readonly<Record<Tradition, FloorTreatment>> = {
  aurelian: { kind: 'alloy', base: '#d9d3c4', joint: '#b9b2a0' },
  borean: { kind: 'alloy', base: '#b4bec3', joint: '#8a959b' },
  neutral: { kind: 'alloy', base: '#cfd2cd', joint: '#a9ada8' },
};
const WOOD_FLOOR: Readonly<Record<Tradition, FloorTreatment>> = {
  aurelian: { kind: 'plank', base: '#b08a5a', joint: '#6b5238' },
  borean: { kind: 'plank', base: '#6a4e38', joint: '#2f2219' },
  neutral: { kind: 'plank', base: '#96714c', joint: ROLES.darkOiled },
};
/** A treatment surface's clean reference (the clinic's maintained white — warm or cool with its builder). */
const CLEAN: Readonly<Record<Tradition, string>> = { aurelian: '#f1ece0', borean: '#d5dde0', neutral: '#eceae3' };

/** THE WEAR RULE: where it gathers, and where it never does (a treatment surface is maintained). */
export const WEAR = {
  /** Metres above the floor. */
  baseBand: [0, 0.35] as const, handBand: [0.75, 1.3] as const,
  /** Families whose surfaces are kept clean — no dust, no scuffs, whatever the tradition. */
  never: ['clinic'] as readonly PurposeFamily[],
} as const;

/** The purpose's emphasis over a tradition's construction. Never touches the sign, the glass's role or the identity line. */
/** The clinic's wipe-clean lower panel is mixed toward this pale bio-glass teal (slice H — the panel against the floor). */
export const CLINIC_PANEL = '#7fb0a6';
export function applyEmphasis(tr: Tradition, family: PurposeFamily): Treatment {
  const b = TRADITION_BASE[tr];
  const t: Treatment = { ...b, wall: { ...b.wall }, floor: { ...b.floor }, ceiling: { ...b.ceiling }, trim: { ...b.trim }, stair: { ...b.stair }, glass: { ...b.glass }, wear: { ...b.wear } };
  switch (family) {
    case 'home':
      // soft layers: a warmer, quieter field; a painted wainscot instead of stone; wood underfoot
      t.wall.field = mixHex(b.wall.field, ROLES.cloth.cream, 0.22);
      t.wall.dado = mixHex(mixHex(b.wall.dado, b.wall.field, 0.5), ROLES.cloth.cream, 0.12);
      t.wall.dadoPattern = 'boards';
      t.wall.rivets = false;
      t.floor = { ...WOOD_FLOOR[tr] };
      t.stair = { kind: 'plank', tread: WOOD_FLOOR[tr].base, riser: b.stair.riser };
      t.ceiling.field = mixHex(b.ceiling.field, ROLES.amberSoft, 0.1); // a home's ceiling holds the lamps' warmth
      t.wear = { base: 0.45, hand: 0.5 };
      break;
    case 'social':
      // a cantina / a theatre foyer: oiled wood where people lean, a worn wood floor
      t.wall.dado = WAINSCOT[tr];
      t.wall.dadoPattern = 'boards';
      t.floor = { ...WOOD_FLOOR[tr] };
      t.stair = { kind: 'plank', tread: WOOD_FLOOR[tr].base, riser: b.stair.riser };
      t.wear = { base: 0.7, hand: 0.85 };
      break;
    case 'clinic':
      // maintained clean surfaces: a lighter field, a wipe-clean dado, a sealed pale floor, a coved pale skirting
      t.wall.field = mixHex(b.wall.field, CLEAN[tr], 0.6);
      // THE WIPE-CLEAN PANEL (slice H, 2026-10-02): the lower wall a clear pale teal-grey — it read as the floor's own tone
      // (luminance within 0.01–0.04 of the sealed floor in every tradition: the room was one pale box)
      t.wall.dado = mixHex(t.wall.field, CLINIC_PANEL, 0.5);
      t.wall.dadoPattern = 'plain';
      t.wall.skirting = mixHex(b.wall.frame, CLEAN[tr], 0.45);
      t.wall.capLine = '#5aa89a'; // a hygiene line in the bio-glass teal (the medicine SYMBOL stays the asset's)
      t.wall.rivets = false;
      t.wall.pilaster = false;
      t.floor = { ...CLEAN_FLOOR[tr] };
      t.stair = { kind: 'alloy', tread: CLEAN_FLOOR[tr].base, riser: t.wall.skirting };
      t.ceiling.field = mixHex(b.ceiling.field, CLEAN[tr], 0.5);
      t.wear = { base: 0, hand: 0 };
      break;
    case 'work':
      // a darker service base: the lower wall takes the knocks; a sealed floor; plain structure
      t.wall.field = mixHex(b.wall.field, REFERENCE_PALETTE.dust, 0.12);
      t.wall.dado = mixHex(b.wall.dado, ROLES.gunmetal, 0.5);
      t.wall.dadoPattern = 'kick';
      t.wall.rivets = true;
      t.wall.pilaster = false;
      t.floor = { ...SEALED_FLOOR[tr] };
      t.stair = { kind: 'alloy', tread: SEALED_FLOOR[tr].base, riser: ROLES.gunmetalDark };
      t.wear = { base: 1, hand: 0.9 };
      break;
    case 'civic':
      // stone and oiled wood: the builder's dressed stone as the dado, a dark oiled rail, a stone floor
      t.wall.dado = CIVIC_STONE[tr];
      t.wall.dadoPattern = 'blocks';
      t.wall.skirting = ROLES.darkOiled;
      t.wall.frame = tr === 'borean' ? b.wall.frame : ROLES.darkOiled;
      t.floor = { ...STONE_FLOOR[tr] };
      t.stair = { kind: 'stone', tread: mixHex(STONE_FLOOR[tr].base, '#000000', 0.12), riser: CIVIC_STONE[tr] };
      t.trim.body = tr === 'borean' ? b.trim.body : ROLES.darkOiled;
      t.wear = { base: 0.3, hand: 0.35 };
      break;
    case 'trade':
      // a shop, a bank, a shuttleport: a hard-wearing floor, a kick plate, the plain field
      t.wall.dadoPattern = 'kick';
      t.floor = { ...STONE_FLOOR[tr] };
      t.stair = { kind: 'stone', tread: mixHex(STONE_FLOOR[tr].base, '#000000', 0.12), riser: b.stair.riser };
      t.wear = { base: 0.6, hand: 0.6 };
      break;
    case 'plain':
      break;
  }
  if (WEAR.never.includes(family)) t.wear = { base: 0, hand: 0 };
  return t;
}

// ─── who built it ────────────────────────────────────────────────────────────

/** A builder's allegiance as the rows spell it: a civ, a player civilisation (`p:<id>`), or none. */
export type Allegiance = CivId | '' | `p:${string}`;
/**
 * The construction tradition of an allegiance ALONE: the two peoples build in their own; everything else — unaligned,
 * and a player civilisation that has not chosen — builds in the shared settler-tech. A player civilisation's CHOSEN
 * tradition is a row (`player_civ_style`), so it reaches `styleFor` as `civTradition`, never through this function.
 */
export function traditionOf(civ: string | null | undefined): Tradition {
  return civ === 'a' ? 'aurelian' : civ === 'b' ? 'borean' : 'neutral';
}
/** `p:<civId>` — a player civilisation's allegiance as `city_allegiance.civ` spells it. */
export function isPlayerCiv(civ: string | null | undefined): boolean { return typeof civ === 'string' && /^p:\d+$/.test(civ); }
/** The civ id of a `p:<civId>` allegiance, else null. */
export function playerCivIdOf(civ: string | null | undefined): bigint | null {
  const m = typeof civ === 'string' ? /^p:(\d+)$/.exec(civ) : null;
  return m ? BigInt(m[1]) : null;
}
/** A tradition word as a row spells it (`player_civ_style.tradition`), validated — anything else is null. */
export function asTradition(v: unknown): Tradition | null {
  return v === 'aurelian' || v === 'borean' || v === 'neutral' ? v : null;
}

// ─── a player civilisation's banner, as PAINT ────────────────────────────────

/** style.md §23: base colour stays between about 30 and 240 sRGB — pure black and pure white never appear. */
export const ACCENT_ALBEDO_MIN = 30;
export const ACCENT_ALBEDO_MAX = 240;
/** HSV saturation cap: the frontier's most saturated PAINT is rust (0.77). Past it a colour reads as a light, and
 *  style.md §3 says never neon magenta / cyan. */
export const ACCENT_MAX_SATURATION = 0.75;
/** A fully saturated paint is not at full value: the value ceiling falls from 240 (a grey) to this (rust's own 180). */
export const ACCENT_VALUE_AT_MAX_SATURATION = 180;
/** A player civilisation's banner as it is DRAWN indoors. */
export interface BannerAccent {
  /** The banner as paint: saturation capped, value capped by saturation, every channel inside the albedo range. */
  paint: string;
  /** The banner the row holds (lower-case `#rrggbb`). */
  source: string;
}
/**
 * THE BANNER ACCENT (the owner's rule C, 2026-10-01): a player civilisation's banner is free-form, so what is DRAWN is
 * derived — hue kept, saturation capped so a neon banner reads as paint and not as light, value capped the more
 * saturated it is, every channel clamped into the albedo range. PURE and deterministic: the same banner gives the same
 * accent in every room of the civilisation (seeded variation never touches it). An invalid or missing hex → null: the
 * caller keeps the tradition's own trim. The accent is a LINE colour (the trim's cap, the wall's identity line, a
 * sign's frame, a plate's frame, the lift's call ring) — never a wall field, a floor or a ceiling, and never the ink.
 */
export function bannerAccent(hex: string | null | undefined): BannerAccent | null {
  if (typeof hex !== 'string') return null;
  const m = /^#([0-9a-fA-F]{6})$/.exec(hex.trim());
  if (!m) return null;
  const source = `#${m[1].toLowerCase()}`;
  const [r, g, b] = hexToRgb(source);
  const mx = Math.max(r, g, b), mn = Math.min(r, g, b), d = mx - mn;
  // hue as a sextant position 0..6 (0 when grey)
  let h = 0;
  if (d > 0) h = mx === r ? ((g - b) / d + 6) % 6 : mx === g ? (b - r) / d + 2 : (r - g) / d + 4;
  const sat = Math.min(mx === 0 ? 0 : d / mx, ACCENT_MAX_SATURATION);
  const vCap = ACCENT_ALBEDO_MAX - (ACCENT_ALBEDO_MAX - ACCENT_VALUE_AT_MAX_SATURATION) * (sat / ACCENT_MAX_SATURATION);
  const v = Math.min(mx, vCap);
  const c = v * sat, x = c * (1 - Math.abs((h % 2) - 1)), lo = v - c;
  const i = Math.floor(h) % 6;
  const rgb: Rgb = i === 0 ? [c, x, 0] : i === 1 ? [x, c, 0] : i === 2 ? [0, c, x] : i === 3 ? [0, x, c] : i === 4 ? [x, 0, c] : [c, 0, x];
  const cl = (n: number): number => Math.max(ACCENT_ALBEDO_MIN, Math.min(ACCENT_ALBEDO_MAX, Math.round(n + lo)));
  return { paint: rgbToHex(cl(rgb[0]), cl(rgb[1]), cl(rgb[2])), source };
}

/** The pack a settlement building was drawn from IS its construction tradition (it survives a conquest). */
export function packCivOf(key: string): CivId | null {
  if (key.startsWith('structure:civa_')) return 'a';
  if (key.startsWith('structure:civb_')) return 'b';
  return null;
}

// ─── the resolver ────────────────────────────────────────────────────────────

/** What a look may be resolved from. Everything past `purpose` is optional — a bare look resolves to neutral. */
export interface StyleInput {
  id: string; key: string; purpose: InteriorPurpose;
  /** The builder's allegiance when the visit knows it (rows); the pack prefix of `key` wins for a settlement building. */
  civ?: string | null;
  /** An explicit tradition (a bed, a preview) — outranks civ and pack. */
  tradition?: Tradition;
  /** The tradition a PLAYER CIVILISATION chose (`player_civ_style`; read only when `civ` is `p:<id>`; unset = neutral). */
  civTradition?: Tradition | null;
  /** A PLAYER CIVILISATION's banner (`player_civ.banner`, `#rrggbb`; read only when `civ` is `p:<id>`). */
  banner?: string | null;
  seed?: number;
}
export interface InteriorStyle extends Treatment {
  tradition: Tradition; family: PurposeFamily;
  /** The key the shared canvases are cached by: everything the DRAWING depends on. Variation is NOT in it. */
  signature: string;
  /** The seeded local variation: a value nudge (±3 %) and a pattern phase (0..1). Never a role, never a meaning. */
  variation: { value: number; phase: number };
  /** A player civilisation's banner as drawn — null for the two peoples (their identity is never themed), for a
   *  settlement pack and for every builder with no banner. The SAME in every room of that civilisation. */
  accent: BannerAccent | null;
  /** The wall's identity line as a MATERIAL colour over the shared canvas (which keeps the tradition's own line):
   *  the accent's paint, or null = the canvas as drawn. A clinic's hygiene line is never replaced. */
  wallLine: string | null;
}

export function seedOf(id: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < id.length; i++) { h ^= id.charCodeAt(i); h = Math.imul(h, 0x01000193); }
  // the murmur finalizer: bare FNV is arithmetic in its last character (neighbouring ids would vary alike)
  h ^= h >>> 16; h = Math.imul(h, 0x85ebca6b); h ^= h >>> 13; h = Math.imul(h, 0xc2b2ae35); h ^= h >>> 16;
  return h >>> 0;
}
export const VARIATION_VALUE = 0.03;

/**
 * Resolve a room's style: source pack / civ → owner treatment → purpose emphasis → the seeded local variation.
 * A PLAYER CIVILISATION (`civ` = `p:<id>`, never a settlement pack) builds in the tradition it CHOSE (`civTradition`,
 * neutral when unset) and wears its own banner as the ACCENT: the trim's cap, the wall's identity line, the sign's
 * and the plate's frame, the lift's call ring — lines only. The two peoples' identity colours are never themed.
 * The signature (the canvas cache key) does not carry the accent: it is a material colour, never a new canvas set.
 */
export function styleFor(look: StyleInput): InteriorStyle {
  const pack = packCivOf(look.key);
  const pciv = !pack && isPlayerCiv(look.civ);
  const tradition = look.tradition ?? (pack ? traditionOf(pack) : pciv ? (asTradition(look.civTradition) ?? 'neutral') : traditionOf(look.civ ?? null));
  const family = PURPOSE_FAMILY[look.purpose] ?? 'plain';
  const t = applyEmphasis(tradition, family);
  const accent = pciv ? bannerAccent(look.banner) : null;
  if (accent) {
    t.trim.cap = accent.paint;
    t.sign = { ...t.sign, frame: accent.paint };
    t.partition = { ...t.partition, plate: { ...t.partition.plate, frame: accent.paint } };
    t.lift = { ...t.lift, ring: accent.paint };
  }
  const s = (look.seed ?? seedOf(look.id)) >>> 0;
  const value = 1 + (((s & 0xffff) / 0xffff) * 2 - 1) * VARIATION_VALUE;
  const phase = ((s >>> 16) & 0xff) / 256;
  return {
    ...t, tradition, family, signature: `${tradition}|${family}`, variation: { value: +value.toFixed(4), phase: +phase.toFixed(4) },
    accent, wallLine: accent && family !== 'clinic' ? accent.paint : null,
  };
}

// ─── the sign ────────────────────────────────────────────────────────────────

/** Sentence case (style.md §11: "Sentence case everywhere"): the first letter only. Never ALL CAPS. */
export function sentenceCase(s: string): string {
  const t = s.trim().replace(/\s+/g, ' ');
  if (!t) return '';
  const lower = t === t.toUpperCase() ? t.toLowerCase() : t;
  return lower.charAt(0).toUpperCase() + lower.slice(1);
}
/** The town venues whose buildings carry a canonical name in the civ's own voice (style.md §6.2; `shared/town_layout.ts`). */
export type VenueKind = 'tavern' | 'shrine' | 'guardpost' | 'bathhouse' | 'capital_core';
export const VENUE_NAMES: Readonly<Record<VenueKind, Readonly<Record<CivId, string>>>> = {
  tavern: { a: 'The Radiant Cup', b: 'The Frosthearth' },
  shrine: { a: 'Shrine of the Dawn Accord', b: 'The Aurora Sanctum' },
  guardpost: { a: 'Warden Post', b: 'Watchspire' },
  bathhouse: { a: 'The Amber Baths', b: 'The Steamhalls' },
  capital_core: { a: 'Concord Heart', b: 'Grand Gatherhall' },
};
/** The canonical name of a venue — ONLY where the building really is that venue, and only for the two peoples. */
export function venueName(venue: VenueKind | null | undefined, civ: string | null | undefined): string | null {
  if (!venue || (civ !== 'a' && civ !== 'b')) return null;
  return VENUE_NAMES[venue]?.[civ] ?? null;
}
export const SIGN_MAX_CHARS = 26;
/**
 * What the sign inside the door reads: the player's OWN name for the building always wins; then the canonical venue
 * name when the building is known to be that venue; else the purpose in sentence case. Text stays dynamic — never baked.
 */
export function signText(label: string, o: { title?: string | null; venue?: VenueKind | null; civ?: string | null } = {}): string {
  const clip = (s: string): string => (s.length > SIGN_MAX_CHARS ? `${s.slice(0, SIGN_MAX_CHARS - 1).trimEnd()}…` : s);
  const own = (o.title ?? '').trim().replace(/\s+/g, ' ');
  if (own) return clip(own);
  const v = venueName(o.venue, o.civ);
  if (v) return clip(v);
  return clip(sentenceCase(label));
}

// ─── the room's sound: a family by purpose and volume ────────────────────────

/** The acoustic families (`lib/audio/mix_math.ts ROOM_PRESETS` holds their numbers). */
export type RoomFamily = 'home' | 'bay' | 'hall' | 'clinic' | 'bank' | 'factory' | 'civic';
export const ROOM_FAMILIES: readonly RoomFamily[] = ['home', 'bay', 'hall', 'clinic', 'bank', 'factory', 'civic'];
/** Room volume in m³ from the shell's half extents and floors (one storey is 3.4 m; the stairwell joins them). */
export function roomVolume(hw: number, hd: number, floors: number): number { return Math.max(1, 2 * hw) * Math.max(1, 2 * hd) * 3.4 * Math.max(1, floors); }
/**
 * A soft home · a damped Borean bay · an open Aurelian hall · a clinic's quiet ventilation · a hard bank hall · a
 * factory floor · a large civic hall. Chosen by purpose, then by who built it, then corrected by VOLUME: a small room
 * never rings like a hall, and a very large open room is a hall whatever it sells.
 */
export function roomFamilyFor(purpose: InteriorPurpose, tradition: Tradition, volumeM3: number): RoomFamily {
  const fam = PURPOSE_FAMILY[purpose] ?? 'plain';
  let room: RoomFamily;
  if (fam === 'home') room = 'home';
  else if (fam === 'clinic') room = 'clinic';
  else if (fam === 'work') room = 'factory';
  else if (purpose === 'bank' || purpose === 'transit') room = 'bank';
  else if (fam === 'civic') room = 'civic';
  else room = tradition === 'borean' ? 'bay' : 'hall'; // social, trade, plain
  // volume corrections
  if (volumeM3 < 220 && (room === 'hall' || room === 'bank' || room === 'civic')) room = tradition === 'borean' ? 'bay' : 'home';
  if (volumeM3 < 160 && room === 'factory') room = 'bay';
  if (volumeM3 > 9000 && (room === 'hall' || room === 'bay')) room = 'civic';
  return room;
}

// ─── the quality overlay ─────────────────────────────────────────────────────

/** `TierPreset.interiorDetail`: 0 essential · 1 standard · 2 full. NEVER topology, anchors, seats, owned pieces or floors. */
export type InteriorDetail = 0 | 1 | 2;
export const INTERIOR_DETAIL_TEX: Readonly<Record<InteriorDetail, number>> = { 0: 128, 1: 256, 2: 512 };
export function clampDetail(v: unknown): InteriorDetail { return v === 0 || v === 1 || v === 2 ? v : 1; }
/** What each level switches (the one table slices C and H read). */
export const INTERIOR_DETAIL_RULES: Readonly<Record<InteriorDetail, { texturePx: number; wear: boolean; emissive: boolean; roomAudio: 'generic' | 'preset'; bed: boolean; detailDecor: boolean }>> = {
  0: { texturePx: 128, wear: false, emissive: false, roomAudio: 'generic', bed: false, detailDecor: false },
  1: { texturePx: 256, wear: true, emissive: true, roomAudio: 'preset', bed: true, detailDecor: false },
  2: { texturePx: 512, wear: true, emissive: true, roomAudio: 'preset', bed: true, detailDecor: true },
};

// ─── the lever, and the room the player is in ────────────────────────────────

export const LOOK_LEVER_KEY = 'atlas:interior-look';
/** `localStorage atlas:interior-look = 'off'` → the pre-programme look, sign and room response, exactly. */
export function interiorLookEnabled(read?: (k: string) => string | null): boolean {
  try {
    const get = read ?? ((k: string) => (typeof localStorage === 'undefined' ? null : localStorage.getItem(k)));
    return get(LOOK_LEVER_KEY) !== 'off';
  } catch { return true; }
}

/** The narrower lever (slice D2): `localStorage atlas:interior-civ-look = 'off'` → a player civilisation builds
 *  neutral with no accent, exactly as before its tradition and banner were read. */
export const CIV_LOOK_LEVER_KEY = 'atlas:interior-civ-look';
export function civLookEnabled(read?: (k: string) => string | null): boolean {
  try {
    const get = read ?? ((k: string) => (typeof localStorage === 'undefined' ? null : localStorage.getItem(k)));
    return get(CIV_LOOK_LEVER_KEY) !== 'off';
  } catch { return true; }
}

/** The room the visit last dressed (the audio runtime reads it; valid only while its id is the player's interior). */
export interface ActiveRoom { id: string; room: RoomFamily; doorX: number; doorZ: number; hw: number; hd: number; tradition: Tradition; family: PurposeFamily }
const GI = globalThis as unknown as { __atlasActiveRoom?: ActiveRoom | null }; // THE INSTANCE-SPLIT LAW: one store whatever the module graph
export function setActiveRoom(r: ActiveRoom | null): void { GI.__atlasActiveRoom = r; }
export function activeRoom(interiorId: string | null | undefined): ActiveRoom | null {
  const r = GI.__atlasActiveRoom;
  return r && interiorId && r.id === interiorId ? r : null;
}

// ─── A BODY IN THE ROOM (slice H, 2026-10-02 — lib/indoor_body.ts) ─────────────────────────────────────────────────
/** How much of the strip light's colour a body indoors takes (the rest is white): a warm cast, never an orange body. */
export const BODY_STRIP_SHARE = 0.16;
/** The room's light level on a body: the unlit room draws its surfaces at their albedo, so a body does too. */
export const BODY_LIGHT_LEVEL = 0.92; // decided with pictures (the indoor body bed): at 1 a pale costume washed out beside the furniture
const srgbToLinear = (c: number): number => (c <= 0.04045 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4));
/**
 * The light a body wears inside a room of this style: the tradition's strip light (amber in every tradition — people
 * live here) mixed `BODY_STRIP_SHARE` into white, in the LINEAR working space, at `BODY_LIGHT_LEVEL`. The same in
 * every room of one tradition and purpose; the same day and night (interiors are unlit — law 9).
 */
export function bodyLightOf(style: Pick<InteriorStyle, 'trim'>): { tint: [number, number, number]; level: number } {
  const hex = /^#?([0-9a-f]{6})$/i.exec(style.trim.strip)?.[1] ?? 'ffb45e';
  const n = parseInt(hex, 16);
  const ch = [(n >> 16) & 255, (n >> 8) & 255, n & 255].map((v) => srgbToLinear(v / 255));
  const peak = Math.max(...ch, 1e-6);
  // the strip's HUE at full value (a dim strip colour must not darken the body — the level is the level)
  const tint = ch.map((v) => (1 - BODY_STRIP_SHARE) + BODY_STRIP_SHARE * (v / peak)) as [number, number, number];
  return { tint, level: BODY_LIGHT_LEVEL };
}
